import { GenData, Random } from '@educraft/base';
import {
  PageGen,
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
} from '../pdf';
import { GUtil } from '../gutil';
import '../draw/primitives';
import '../draw/string';
import '../draw/helpers';

/**
 * MiniLUK color scheme for tiles
 */
export enum EMiniLukColor {
  Red = 0,
  Green = 1,
  Blue = 2,
}

/**
 * MiniLUK tile back pattern type
 */
export enum EMiniLukBack {
  Full = 0,
  Left = 1,
  Right = 2,
}

/**
 * MiniLUK tile configuration
 */
export interface MiniLukTile {
  index: number;
  color: EMiniLukColor;
  top: EMiniLukBack;
  bottom: EMiniLukBack;
}

/**
 * MiniLUK grouped item
 */
export interface MiniLukGroup<T> {
  obj: T;
  group: number;
}

/**
 * MiniLUK drawing data passed to callbacks
 */
export interface MiniLukData<T> {
  page: PageGen;
  rect: PdfRect;
  obj: T;
  isAnswer: boolean;
  index: number;
}

/**
 * MiniLUK color brushes
 */
export const MiniLukColors: Record<EMiniLukColor, PdfBrush> = {
  [EMiniLukColor.Red]: new PdfBrush(PdfColor.fromHex('#FF0000')),
  [EMiniLukColor.Green]: new PdfBrush(PdfColor.fromHex('#00FF00')),
  [EMiniLukColor.Blue]: new PdfBrush(PdfColor.fromHex('#0000FF')),
};

/**
 * MiniLUK background colors for groups (light tints)
 */
export const MiniLukGroupColors: PdfBrush[] = [
  new PdfBrush(PdfColor.fromHex('#FFCCCC')), // Light red
  new PdfBrush(PdfColor.fromHex('#CCFFCC')), // Light green
  new PdfBrush(PdfColor.fromHex('#CCCCFF')), // Light blue
  new PdfBrush(PdfColor.fromHex('#FFFFCC')), // Light yellow
  new PdfBrush(PdfColor.fromHex('#FFCCFF')), // Light magenta
  new PdfBrush(PdfColor.fromHex('#CCFFFF')), // Light cyan
];

/**
 * Generate MiniLUK puzzle data by grouping items by answer.
 *
 * Port of C# G<T>.GenMiniLUK()
 *
 * @param generate Function to generate a new item
 * @param answerFunc Function to extract answer from item (used for grouping)
 * @param questionFunc Function to extract question from item (for uniqueness)
 * @param counts Valid group configurations (e.g., [12, 6, 4, 3, 2])
 * @param random Random instance
 * @returns Array of grouped items
 */
export function genMiniLuk<T>(
  generate: () => T,
  answerFunc: (item: T) => string,
  questionFunc: (item: T) => string,
  counts: Set<number>,
  random: Random
): MiniLukGroup<T>[] {
  const dictionary = new Map<string, Map<string, T>>();
  let counter = 0;

  while (true) {
    const item = generate();
    const answer = answerFunc(item);
    const question = questionFunc(item);

    if (!dictionary.has(answer)) {
      dictionary.set(answer, new Map());
    }

    const answerMap = dictionary.get(answer)!;
    if (!answerMap.has(question)) {
      counter = 0;
      answerMap.set(question, item);
    }

    // Check each valid count configuration
    for (const count1 of counts) {
      const count2 = 12 / count1;
      const items = Array.from(dictionary.entries()).filter(([_, v]) => v.size >= count2);

      if (items.length >= count1) {
        const list: MiniLukGroup<T>[] = [];
        const rr1 = shuffleArray(items, random).slice(0, count1);

        for (const [_, itemMap] of rr1) {
          const values = Array.from(itemMap.values());
          const rr2 = shuffleArray(values, random).slice(0, count2);

          for (let i = 0; i < rr2.length; i++) {
            list.push({ obj: rr2[i], group: i });
          }
        }

        return list;
      }
    }

    if (counter++ > 10_000) {
      throw new Error('genMiniLuk: timeout exceeded');
    }
  }
}

/**
 * Shuffle array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[], random: Random): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = random.next(0, i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Generate permutations of a list
 */
function generatePermutations(list: number[], start: number, result: number[][]): void {
  if (start === list.length) {
    result.push([...list]);
    return;
  }

  for (let i = start; i < list.length; i++) {
    [list[start], list[i]] = [list[i], list[start]];
    generatePermutations(list, start + 1, result);
    [list[start], list[i]] = [list[i], list[start]];
  }
}

/**
 * Generate MiniLUK tile configuration
 */
function genTiles(random: Random): MiniLukTile[][] {
  const numbers = [0, 1, 2, 3];
  const permutations: number[][] = [];
  generatePermutations(numbers, 0, permutations);

  const allTiles: MiniLukTile[] = [
    { index: 0, color: EMiniLukColor.Green, top: EMiniLukBack.Full, bottom: EMiniLukBack.Right },
    { index: 1, color: EMiniLukColor.Blue, top: EMiniLukBack.Full, bottom: EMiniLukBack.Right },
    { index: 2, color: EMiniLukColor.Red, top: EMiniLukBack.Full, bottom: EMiniLukBack.Left },
    { index: 3, color: EMiniLukColor.Green, top: EMiniLukBack.Full, bottom: EMiniLukBack.Left },
    { index: 4, color: EMiniLukColor.Red, top: EMiniLukBack.Left, bottom: EMiniLukBack.Full },
    { index: 5, color: EMiniLukColor.Blue, top: EMiniLukBack.Full, bottom: EMiniLukBack.Left },
    { index: 6, color: EMiniLukColor.Green, top: EMiniLukBack.Left, bottom: EMiniLukBack.Full },
    { index: 7, color: EMiniLukColor.Blue, top: EMiniLukBack.Right, bottom: EMiniLukBack.Full },
    { index: 8, color: EMiniLukColor.Red, top: EMiniLukBack.Full, bottom: EMiniLukBack.Right },
    { index: 9, color: EMiniLukColor.Green, top: EMiniLukBack.Right, bottom: EMiniLukBack.Full },
    { index: 10, color: EMiniLukColor.Red, top: EMiniLukBack.Right, bottom: EMiniLukBack.Full },
    { index: 11, color: EMiniLukColor.Blue, top: EMiniLukBack.Left, bottom: EMiniLukBack.Full },
  ];

  const result: MiniLukTile[][] = [new Array(6), new Array(6)];

  const colors = shuffleArray([EMiniLukColor.Red, EMiniLukColor.Green, EMiniLukColor.Blue], random);

  for (let c = 0; c < colors.length; c++) {
    const colorTiles = allTiles.filter((t) => t.color === colors[c]);
    const list: number[][] = [];

    for (const perm of permutations) {
      if (
        colorTiles[perm[0]].bottom === colorTiles[perm[1]].top &&
        colorTiles[perm[2]].bottom === colorTiles[perm[3]].top
      ) {
        list.push(perm);
      }
    }

    const item = list[random.next(0, list.length)];

    result[1][c * 2 + 0] = colorTiles[item[0]];
    result[0][c * 2 + 0] = colorTiles[item[1]];
    result[1][c * 2 + 1] = colorTiles[item[2]];
    result[0][c * 2 + 1] = colorTiles[item[3]];
  }

  return result;
}

/**
 * Draw a complete MiniLUK puzzle.
 *
 * Port of C# G<T>.DrawMiniLUK()
 *
 * @param page The page to draw on
 * @param items The grouped items to display
 * @param drawQuestion Callback to draw question content
 * @param drawAnswer Callback to draw answer content
 * @param widthCalculated Callback when width is calculated (for font adjustment)
 * @param data GenData instance (contains random and lang)
 */
export function drawMiniLuk<T>(
  page: PageGen,
  items: MiniLukGroup<T>[],
  drawQuestion: (data: MiniLukData<T>) => void,
  drawAnswer: (data: MiniLukData<T>) => void,
  widthCalculated: ((width: number) => void) | null,
  data: GenData
): void {
  const margin = PdfUnit.FromMillimeters(8);
  const margin2 = PdfUnit.FromMillimeters(3);
  const marginInner = PdfUnit.FromMillimeters(1);

  const baseSize = PdfUnit.FromMillimeters(242) / 6;
  const size = new PdfSize(baseSize, baseSize);
  const origin1 = new PdfPoint(
    (PdfSize.A4H.width - size.width * 6) / 2,
    PdfSize.A4H.height - margin - 2 * size.height
  );
  const origin2 = new PdfPoint(origin1.x, margin);
  const labelSize = new PdfSize(PdfUnit.FromMillimeters(6), PdfUnit.FromMillimeters(6));

  const baseMiniSize = (PdfSize.A4H.height - 2 * margin - 2 * margin2 - 4 * size.height) / 2;
  const miniOrigin = new PdfPoint(
    origin2.x + size.width * 6 - 6 * baseMiniSize,
    origin2.y + 2 * size.height + margin2
  );

  const headerRect = new PdfRect(
    origin2.x,
    miniOrigin.y,
    miniOrigin.x - origin2.x - margin2,
    baseMiniSize * 2
  );

  const labelFont = new PdfFont(PdfFont.FONT_NAME, 12);

  const headers: string[] = [];
  const tiles = genTiles(data.random);

  const array = shuffleArray(items, data.random);
  const hasBackground = new Set(array.map((i) => i.group)).size > 1;

  widthCalculated?.(baseSize);

  headers.push(data.lang.Logico_Match ?? 'Match.');
  if (hasBackground) {
    headers.push(data.lang.MiniLuk_BackgroundMatter ?? 'The colored background matters.');
  }

  const colorBrushes: Record<EMiniLukColor, PdfBrush> = {
    [EMiniLukColor.Red]: new PdfBrush(PdfColor.fromRgb(255, 0, 0)),
    [EMiniLukColor.Green]: new PdfBrush(PdfColor.fromRgb(0, 255, 0)),
    [EMiniLukColor.Blue]: new PdfBrush(PdfColor.fromRgb(0, 0, 255)),
  };

  // Draw header
  page.drawStrings(
    headers,
    GUtil.baseFont,
    PdfBrush.Black,
    null,
    headerRect,
    ETextAlign.CenterLeft
  );

  // Draw grid
  for (let y = 0; y < 2; y++) {
    for (let x = 0; x < 6; x++) {
      const rect1 = new PdfRect(
        origin1.x + x * size.width,
        origin1.y + y * size.height,
        size.width,
        size.height
      );
      const rect2 = new PdfRect(
        origin2.x + x * size.width,
        origin2.y + y * size.height,
        size.width,
        size.height
      );
      const rect2N = new PdfRect(rect2.x, rect2.y, labelSize.width, labelSize.height);
      const rectMini = new PdfRect(
        miniOrigin.x + x * baseMiniSize,
        miniOrigin.y + (1 - y) * baseMiniSize,
        baseMiniSize,
        baseMiniSize
      );
      const rectMiniInner = new PdfRect(
        rectMini.x + marginInner,
        rectMini.y + marginInner,
        rectMini.width - 2 * marginInner,
        rectMini.height - 2 * marginInner
      );

      const tile = tiles[y][x];
      const index1 = tile.index;
      const index2 = y * 6 + x;

      const data1: MiniLukData<T> = {
        page,
        rect: rect1,
        obj: array[index1].obj,
        isAnswer: true,
        index: index1,
      };
      const data2: MiniLukData<T> = {
        page,
        rect: rect2,
        obj: array[index2].obj,
        isAnswer: false,
        index: index2,
      };

      // Draw backgrounds if multiple groups
      if (hasBackground) {
        page.drawRectangle(null, MiniLukGroupColors[array[tile.index].group], rect1);
        page.drawRectangle(null, MiniLukGroupColors[array[index2].group], rect2);
      }

      // Draw content
      drawAnswer(data1);
      drawQuestion(data2);

      // Build tile polygon points
      const pp: PdfPoint[] = [];
      const centerTop = new PdfPoint(rectMiniInner.centerX, rectMiniInner.top);
      const centerBottom = new PdfPoint(rectMiniInner.centerX, rectMiniInner.bottom);

      if (tile.top === EMiniLukBack.Left) {
        pp.push(new PdfPoint(rectMiniInner.left, rectMiniInner.top));
        pp.push(centerTop);
      } else if (tile.top === EMiniLukBack.Right) {
        pp.push(centerTop);
        pp.push(new PdfPoint(rectMiniInner.right, rectMiniInner.top));
      } else {
        pp.push(new PdfPoint(rectMiniInner.left, rectMiniInner.top));
        pp.push(new PdfPoint(rectMiniInner.right, rectMiniInner.top));
      }

      if (tile.bottom === EMiniLukBack.Left) {
        pp.push(centerBottom);
        pp.push(new PdfPoint(rectMiniInner.left, rectMiniInner.bottom));
      } else if (tile.bottom === EMiniLukBack.Right) {
        pp.push(new PdfPoint(rectMiniInner.right, rectMiniInner.bottom));
        pp.push(centerBottom);
      } else {
        pp.push(new PdfPoint(rectMiniInner.right, rectMiniInner.bottom));
        pp.push(new PdfPoint(rectMiniInner.left, rectMiniInner.bottom));
      }

      // Draw number label
      page.drawBackground(rect2N);
      page.drawString(
        `${y * 6 + x + 1}.`,
        labelFont,
        PdfBrush.Black,
        null,
        rect2N,
        ETextAlign.Center
      );
      page.drawRectangle(GUtil.basePen, null, rect2N);

      // Draw borders
      page.drawRectangle(GUtil.basePen, null, rect1);
      page.drawRectangle(GUtil.basePen, null, rect2);

      // Draw mini tile
      const tileBackground = new PdfBrush(PdfColor.fromRgb(255, 255, 220));
      page.drawRectangle(null, tileBackground, rectMini);
      page.drawPolygon(null, colorBrushes[tile.color], pp);
      page.drawRectangle(GUtil.basePen, null, rectMini);
    }
  }
}
